// Lead detail "Marketing strategy" field, directly after Source (round three
// #6, mockup Additions.tsx AttributionField). Picking a strategy stores a
// manual attribution; "No strategy" clears it. The note says how the lead
// got its strategy so an auto match is never mistaken for a choice.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import { useIsMobile } from '../lib/useIsMobile';
import type { MarketingOptionsResponse } from '../../shared/marketing';
import type { LeadWithNames } from '../../shared/types';
import { FieldRow } from './FieldSheet';
import { PickerSheet } from './PickerSheet';
import { InlineSelect } from './primitives';
import './MarketingStrategyField.css';

const NONE = '__none';

export interface StrategyPick {
  strategy_id: string | null;
  strategy_name: string | null;
  strategy_channel_name: string | null;
}

type LeadAttribution = Pick<LeadWithNames, 'strategy_id' | 'attribution' | 'strategy_name' | 'strategy_channel_name' | 'campaign'>;

export function attributionNote(lead: LeadAttribution): string {
  if (!lead.strategy_id) return 'Not counted in Marketing results';
  if (lead.attribution === 'auto') return lead.campaign ? `Auto · campaign “${lead.campaign}”` : 'Auto';
  return 'Picked by you';
}

export function MarketingStrategyField({ lead, onSave }: { lead: LeadAttribution; onSave: (pick: StrategyPick) => void }) {
  const mobile = useIsMobile();
  const [options, setOptions] = useState<MarketingOptionsResponse['strategies'] | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let live = true;
    api<MarketingOptionsResponse>('/api/admin/marketing/options')
      .then((o) => { if (live) setOptions(o.strategies); })
      .catch((e) => reportError(e, 'Could not load marketing strategies.'));
    return () => { live = false; };
  }, []);

  // Grouped per channel for the phone picker; the desktop menu keeps the
  // same order, so each channel's strategies still sit together.
  const items = [
    { value: NONE, label: 'No strategy', group: 'None' },
    ...[...(options ?? [])]
      .sort((a, b) => (a.channel_name ?? '~').localeCompare(b.channel_name ?? '~') || a.name.localeCompare(b.name))
      .map((s) => ({ value: s.id, label: s.name, sub: s.channel_name ?? 'No channel', group: s.channel_name ?? 'No channel' })),
  ];
  // A strategy that is no longer in the picker (an idea, deleted) still shows by name.
  if (lead.strategy_id && options && !options.some((s) => s.id === lead.strategy_id)) {
    items.push({ value: lead.strategy_id, label: lead.strategy_name ?? 'Unknown strategy', sub: lead.strategy_channel_name ?? '', group: lead.strategy_channel_name ?? 'No channel' });
  }

  const save = (value: string) => {
    if (value === (lead.strategy_id ?? NONE)) return;
    const picked = options?.find((s) => s.id === value);
    onSave(value === NONE
      ? { strategy_id: null, strategy_name: null, strategy_channel_name: null }
      : { strategy_id: value, strategy_name: picked?.name ?? null, strategy_channel_name: picked?.channel_name ?? null });
  };

  const note = attributionNote(lead);
  const open_ = lead.strategy_id && <> · <Link to={`/marketing/s/${lead.strategy_id}`}>{mobile ? 'open →' : 'Open →'}</Link></>;

  if (mobile) {
    return (
      <>
        <FieldRow label="Strategy" value={lead.strategy_name ?? ''} empty={!lead.strategy_id} tone="acc" onPress={() => setOpen(true)} />
        <div className="mk-attr-note-m">{note}{open_}</div>
        <PickerSheet open={open} title="Marketing strategy" items={items} value={lead.strategy_id ?? NONE} onSelect={save} onClose={() => setOpen(false)} />
      </>
    );
  }

  return (
    <div className="field mk-attr">
      <div className="field-label">Marketing strategy</div>
      <InlineSelect items={items} value={lead.strategy_id ?? NONE} onSave={save}
        display={lead.strategy_id ? lead.strategy_name ?? 'Unknown strategy' : undefined} />
      <div className="mk-attr-note">
        {note}
        {lead.strategy_channel_name && lead.strategy_id && <span className="mk-attr-chan"> · {lead.strategy_channel_name}</span>}
        {open_}
      </div>
    </div>
  );
}
